import { useCallback } from "react";
import { isValidIndex } from "../utils/ArrayHelpers";
import { KEY_INCREMENTS, getFocusableChildren, getFocusedIndex, isHorizontalKey, isNavigationKey, isVerticalKey, } from "../utils/keyboard";
const ROW_SELECTOR = "[role='row']";
const CELL_SELECTOR = "[role='gridcell'][aria-colindex],[role='columnheader'][aria-colindex]";
const focusOnCellChild = (key, focusableChildren, index) => {
    // when we move left, the last focusable item in the cell is focused
    // when we move vertically we use the same index as the previously focused item
    const indexToFocus = key === "ArrowLeft" ? -1 : isVerticalKey(key) && isValidIndex(focusableChildren, index) ? index : 0;
    const element = focusableChildren.at(indexToFocus);
    if (!element) {
        return;
    }
    element.focus();
};
const getNextFocusable = (key, focusableElements, focusedIndex) => {
    const nextIndex = focusedIndex + KEY_INCREMENTS[key];
    return isValidIndex(focusableElements, nextIndex) ? focusableElements[nextIndex] : null;
};
export const getIntAttribute = (element, attributeName) => {
    const value = element.getAttribute(attributeName);
    return value !== null ? parseInt(value) : null;
};
const getCellCoordinates = (rows, cell) => {
    const column = getIntAttribute(cell, "aria-colindex");
    if (column === null) {
        return null;
    }
    const rowElement = getCurrentRow(cell);
    if (!rowElement) {
        return null;
    }
    const rowIndex = rows.indexOf(rowElement);
    return { column, row: rowIndex + 1 };
};
const getMaxCoordinates = (rows) => {
    if (!rows.length) {
        return null;
    }
    const columns = rows[0].querySelectorAll(CELL_SELECTOR);
    // we use length because the coordinates are 1-based indexes
    return { column: columns.length, row: rows.length };
};
const isValidCoordinates = (coordinates, maxCoordinates) => coordinates.column >= 1 &&
    coordinates.column <= maxCoordinates.column &&
    coordinates.row >= 1 &&
    coordinates.row <= maxCoordinates.row;
const getCellByCoordinates = (rows, { column, row }) => {
    const rowIndex = row - 1;
    const rowElement = isValidIndex(rows, rowIndex) ? rows[rowIndex] : null;
    if (!rowElement) {
        return null;
    }
    const cells = Array.from(rowElement.querySelectorAll(CELL_SELECTOR));
    const columnIndex = column - 1;
    if (!cells.length) {
        return null;
    }
    // if it's trying to get a cell that doesn't exist, use the last one on the row.
    // this helps focusing on cells within incomplete rows, like the "Load more" that only has one cell.
    return (isValidIndex(cells, columnIndex) ? cells[columnIndex] : cells.at(-1)) || null;
};
const getNextHorizontalCoordinates = (increment, { column, row }) => ({
    column: column + increment,
    row,
});
const getNextVerticalCoordinates = (increment, { column, row }) => ({
    column,
    row: row + increment,
});
const getCurrentRow = (cell) => cell.closest(ROW_SELECTOR);
const filterHeaderCellFocusableChildren = (cell, focusableChildren, gridID) => {
    if (cell.role === "columnheader") {
        const textArray = gridID.split("-wrapper");
        const tableId = textArray.shift();
        const actionKebab = focusableChildren.filter((el) => {
            if (tableId && el.id.startsWith("kebab-" + tableId)) {
                return true;
            }
        });
        const customFocusable = focusableChildren.filter((el) => {
            // Look for focusable elements that are not the cell or action menu
            if (!tableId || (!el.id.startsWith(tableId) && !el.id.startsWith("kebab-" + tableId))) {
                return true;
            }
        });
        return customFocusable.length > 0 ? [...customFocusable, ...actionKebab] : focusableChildren;
    }
    return focusableChildren;
};
const getCurrentCell = (gridElement) => document.activeElement && gridElement.contains(document.activeElement)
    ? document.activeElement.closest(CELL_SELECTOR)
    : null;
const findNextCell = (key, grid, previousCell, rowsSelectorPrefix) => {
    const rows = Array.from(grid.querySelectorAll(`${rowsSelectorPrefix} ${ROW_SELECTOR}`));
    const previousCoordinates = getCellCoordinates(rows, previousCell);
    if (!previousCoordinates) {
        return null;
    }
    const nextCoordinatesGetter = isHorizontalKey(key) ? getNextHorizontalCoordinates : getNextVerticalCoordinates;
    const maxCoordinates = getMaxCoordinates(rows);
    if (!maxCoordinates) {
        return null;
    }
    let coordinates = previousCoordinates;
    let cell;
    // if there is no cell with the next coordinates keep incrementing until the last coordinates available
    do {
        coordinates = nextCoordinatesGetter(KEY_INCREMENTS[key], coordinates);
        cell = isValidCoordinates(coordinates, maxCoordinates) ? getCellByCoordinates(rows, coordinates) : null;
    } while (!cell &&
        coordinates.column > 1 &&
        coordinates.column < maxCoordinates.column &&
        coordinates.row > 1 &&
        coordinates.row < maxCoordinates.row);
    if (!cell) {
        return null;
    }
    const focusableChildren = getFocusableChildren(cell);
    return {
        cell,
        focusableChildren: filterHeaderCellFocusableChildren(cell, focusableChildren, grid.id),
    };
};
const findNextCellWithFocusableChildren = (key, grid, initialCell, rowsSelectorPrefix) => {
    let nextCell = initialCell;
    let nextFocusableChildren;
    // loop until we find a cell with focusable items or there are no more cells
    do {
        const result = findNextCell(key, grid, nextCell, rowsSelectorPrefix);
        if (!result) {
            return null;
        }
        nextCell = result.cell;
        nextFocusableChildren = result.focusableChildren;
    } while (!nextFocusableChildren.length);
    return nextFocusableChildren;
};
/**
 * Enables keyboard navigation on the grid element.
 * The element and its children have to define the appropriate ARIA roles and attributes related to a grid:
 * - `role="grid"` on the main element
 * - `role="row"` on each row
 * - `role="columnheader"` on the header cells, if any
 * - `role="gridcell"` on the content cells
 * - `aria-colindex="X"` on each header and cell (**X is an integer >= 1**)
 * - `tabindex="0"` on the elements **within** cells that can be focused
 *
 * `aria-rowindex` is not required for the purposes on this hook (grids may have extra rows
 * that are not part of the data, like the case of the async grouping which has a row with a "Load more" button).
 *
 * Behavior for the Table component:
 * 1. Arrow keys to move around within the table.
 *   a. Each cell has a focus state.
 * 2. Tooltips on header can be dismissed by pressing the ESC key.
 * 3. Actions within a cell should be visible when the cell is in focus.
 * 4. When the focus is on a row, the row should display a hover state.
 * 5. For grouping rows, first focus is on the arrow icon.
 * 6. To select "Load more", users navigate there by using the arrow key.
 * 7. Navigation is not circular: reaching the end of a row doesn't circle back to the beginning.
 */
const useGridNavigation = (options = {}) => {
    const { exitWithTab = true, rowsSelectorPrefix = "" } = options;
    const onKeyDown = useCallback((e) => {
        var _a;
        const gridElement = e.currentTarget;
        if (exitWithTab && e.key === "Tab") {
            (_a = getFocusableChildren(gridElement)
                .at(e.shiftKey ? 0 : -1)) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
        }
        if (!isNavigationKey(e.key)) {
            return;
        }
        e.preventDefault();
        const currentCell = getCurrentCell(gridElement);
        if (!currentCell) {
            return;
        }
        const { focusedIndex, focusableChildren } = getFocusedIndex(currentCell);
        if (isHorizontalKey(e.key)) {
            // navigate within the cell
            const nextFocusableWithinCell = getNextFocusable(e.key, focusableChildren, focusedIndex);
            const filteredHeaderCellChildren = filterHeaderCellFocusableChildren(currentCell, focusableChildren, gridElement.id);
            const shouldFocusNextChildInCell = nextFocusableWithinCell && filteredHeaderCellChildren.includes(nextFocusableWithinCell);
            if (shouldFocusNextChildInCell) {
                nextFocusableWithinCell.focus();
                return;
            }
        }
        const nextFocusableChildren = findNextCellWithFocusableChildren(e.key, gridElement, currentCell, rowsSelectorPrefix);
        if (!nextFocusableChildren) {
            return;
        }
        focusOnCellChild(e.key, nextFocusableChildren, focusedIndex);
    }, [exitWithTab, rowsSelectorPrefix]);
    return {
        gridNavigationProps: {
            onKeyDown,
        },
    };
};
export default useGridNavigation;
//# sourceMappingURL=useGridNavigation.js.map